import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  PictureAsPdf as PictureAsPdfIcon,
  Search as SearchIcon,
  DateRange as DateRangeIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import PDFViewer from '../../components/shared/PDFViewer';
import estadosContablesService, {
  EstadoContableLinea,
  EstadoContableResult,
  EstadoContableSearchParams,
  EstadoResultadosPdfListado
} from '../../services/estadosContablesService';

type ReportType = 'resultados' | 'patrimonial';

interface Props {
  reportType: ReportType;
}

function fmt(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Línea sintética de rubro (API): sin texto en columna Cuenta para no repetir la leyenda "0 - Acumulado de rubro". */
function cuentaColumnLabel(nroCuenta: number, nomCuenta: string): string {
  const nom = nomCuenta.trim();
  if (nroCuenta === 0 && nom.toLowerCase() === 'acumulado de rubro') return '';
  return `${nroCuenta} - ${nomCuenta}`;
}

type RangoFechaPreset = 'mes-actual' | 'mes-pasado' | 'ult-3-meses' | 'ult-6-meses' | 'ult-12-meses' | 'personalizado';

function toYmdLocal(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/** Rango inclusivo de meses calendario; el período termina el último día del mes actual. */
function rangoFromPreset(preset: Exclude<RangoFechaPreset, 'personalizado'>, ref: Date = new Date()): { desde: string; hasta: string } {
  const y = ref.getFullYear();
  const m = ref.getMonth();
  switch (preset) {
    case 'mes-actual':
      return { desde: toYmdLocal(new Date(y, m, 1)), hasta: toYmdLocal(new Date(y, m + 1, 0)) };
    case 'mes-pasado':
      return { desde: toYmdLocal(new Date(y, m - 1, 1)), hasta: toYmdLocal(new Date(y, m, 0)) };
    case 'ult-3-meses':
      return { desde: toYmdLocal(new Date(y, m - 2, 1)), hasta: toYmdLocal(new Date(y, m + 1, 0)) };
    case 'ult-6-meses':
      return { desde: toYmdLocal(new Date(y, m - 5, 1)), hasta: toYmdLocal(new Date(y, m + 1, 0)) };
    case 'ult-12-meses':
      return { desde: toYmdLocal(new Date(y, m - 11, 1)), hasta: toYmdLocal(new Date(y, m + 1, 0)) };
  }
}

type FilaTablaEstado =
  | ({ tipo: 'cuenta' } & EstadoContableLinea)
  | {
      tipo: 'rubro';
      seccion: string;
      nroRubro: number;
      nomRubro: string;
      saldoInicial: number;
      saldoInicialComparativo: number | null;
      saldoPeriodo: number;
      saldoPeriodoComparativo: number | null;
      montoBase: number;
      montoComparativo: number | null;
      variacionAbsoluta: number | null;
      variacionPorcentual: number | null;
    };

function rubroKey(l: { seccion: string; nroRubro: number; nomRubro: string }): string {
  return `${l.seccion}|${l.nroRubro}|${l.nomRubro}`;
}

/** Agrupa líneas detalladas por rubro (misma clave que aggregateRubrosFromLineas). */
function buildLineasPorRubro(lineas: EstadoContableLinea[]): Map<string, EstadoContableLinea[]> {
  const m = new Map<string, EstadoContableLinea[]>();
  for (const line of lineas) {
    const k = rubroKey(line);
    const arr = m.get(k);
    if (arr) arr.push(line);
    else m.set(k, [line]);
  }
  m.forEach((arr) => {
    arr.sort((a, b) => a.nroCuenta - b.nroCuenta);
  });
  return m;
}

/** Acumula líneas por rubro (estado de resultados sin detalle de cuenta). */
function aggregateRubrosFromLineas(lineas: EstadoContableLinea[]): Extract<FilaTablaEstado, { tipo: 'rubro' }>[] {
  const grouped = new Map<
    string,
    {
      seccion: string;
      nroRubro: number;
      nomRubro: string;
      saldoInicial: number;
      saldoInicialComparativo: number | null;
      saldoPeriodo: number;
      saldoPeriodoComparativo: number | null;
      montoBase: number;
      montoComparativo: number | null;
      variacionAbsoluta: number | null;
    }
  >();

  for (const l of lineas) {
    const key = rubroKey(l);
    const prev = grouped.get(key);
    const si = l.saldoInicial ?? 0;
    const sic = l.saldoInicialComparativo;
    const sp = l.saldoPeriodo ?? 0;
    const spc = l.saldoPeriodoComparativo;
    if (!prev) {
      grouped.set(key, {
        seccion: l.seccion,
        nroRubro: l.nroRubro,
        nomRubro: l.nomRubro,
        saldoInicial: si,
        saldoInicialComparativo: sic ?? null,
        saldoPeriodo: sp,
        saldoPeriodoComparativo: spc ?? null,
        montoBase: l.montoBase,
        montoComparativo: l.montoComparativo,
        variacionAbsoluta: l.variacionAbsoluta
      });
    } else {
      prev.saldoInicial += si;
      prev.saldoInicialComparativo =
        prev.saldoInicialComparativo == null && sic == null
          ? null
          : (prev.saldoInicialComparativo ?? 0) + (sic ?? 0);
      prev.saldoPeriodo += sp;
      prev.saldoPeriodoComparativo =
        prev.saldoPeriodoComparativo == null && spc == null
          ? null
          : (prev.saldoPeriodoComparativo ?? 0) + (spc ?? 0);
      prev.montoBase += l.montoBase;
      prev.montoComparativo = (prev.montoComparativo ?? 0) + (l.montoComparativo ?? 0);
      prev.variacionAbsoluta = (prev.variacionAbsoluta ?? 0) + (l.variacionAbsoluta ?? 0);
    }
  }

  return Array.from(grouped.values())
    .map((g) => {
      let varPct: number | null = null;
      if (g.montoComparativo != null && g.montoComparativo !== 0) {
        varPct = ((g.montoBase - g.montoComparativo) / Math.abs(g.montoComparativo)) * 100;
      }
      return {
        tipo: 'rubro' as const,
        seccion: g.seccion,
        nroRubro: g.nroRubro,
        nomRubro: g.nomRubro,
        saldoInicial: g.saldoInicial,
        saldoInicialComparativo: g.saldoInicialComparativo,
        saldoPeriodo: g.saldoPeriodo,
        saldoPeriodoComparativo: g.saldoPeriodoComparativo,
        montoBase: g.montoBase,
        montoComparativo: g.montoComparativo,
        variacionAbsoluta: g.variacionAbsoluta,
        variacionPorcentual: varPct
      };
    })
    .sort((a, b) => {
      if (a.seccion !== b.seccion) return a.seccion.localeCompare(b.seccion);
      return a.nroRubro - b.nroRubro;
    });
}

const PRESET_CHIPS: { id: RangoFechaPreset; label: string }[] = [
  { id: 'mes-actual', label: 'Mes actual' },
  { id: 'mes-pasado', label: 'Mes pasado' },
  { id: 'ult-3-meses', label: 'Últ. 3 meses' },
  { id: 'ult-6-meses', label: 'Últ. 6 meses' },
  { id: 'ult-12-meses', label: 'Últ. 12 meses' },
  { id: 'personalizado', label: 'Personalizado' }
];

const EstadoContableReport: React.FC<Props> = ({ reportType }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const inicialMesActual = useMemo(() => rangoFromPreset('mes-actual', new Date()), []);

  const [fechaDesde, setFechaDesde] = useState(inicialMesActual.desde);
  const [fechaHasta, setFechaHasta] = useState(inicialMesActual.hasta);
  const [rangoPreset, setRangoPreset] = useState<RangoFechaPreset>('mes-actual');
  const [comparar, setComparar] = useState(false);
  const [detallado, setDetallado] = useState(false);
  const [compFechaDesde, setCompFechaDesde] = useState(inicialMesActual.desde);
  const [compFechaHasta, setCompFechaHasta] = useState(inicialMesActual.hasta);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [result, setResult] = useState<EstadoContableResult | null>(null);
  const [expandedRubros, setExpandedRubros] = useState<Set<string>>(() => new Set());

  const title = reportType === 'resultados' ? 'Estado de resultados' : 'Situación patrimonial';
  const to = reportType === 'resultados' ? '/contabilidad/estado-resultados' : '/contabilidad/situacion-patrimonial';

  const params = useMemo<EstadoContableSearchParams>(
    () => ({
      fechaDesde,
      fechaHasta,
      compFechaDesde: comparar ? compFechaDesde : undefined,
      compFechaHasta: comparar ? compFechaHasta : undefined
    }),
    [fechaDesde, fechaHasta, comparar, compFechaDesde, compFechaHasta]
  );

  const paramsPdf = useMemo<EstadoContableSearchParams>(() => ({ ...params, detallado }), [params, detallado]);

  const lineasMostradas = useMemo((): FilaTablaEstado[] => {
    if (!result) return [];
    if (detallado) {
      return result.lineas.map((l) => ({ tipo: 'cuenta' as const, ...l }));
    }
    return aggregateRubrosFromLineas(result.lineas);
  }, [result, detallado]);

  const lineasPorRubro = useMemo(() => (result ? buildLineasPorRubro(result.lineas) : new Map<string, EstadoContableLinea[]>()), [result]);

  useEffect(() => {
    setExpandedRubros(new Set());
  }, [result, detallado, reportType]);

  const toggleRubroExpand = (key: string): void => {
    setExpandedRubros((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const aplicarPresetRango = (preset: RangoFechaPreset): void => {
    setRangoPreset(preset);
    if (preset === 'personalizado') return;
    const r = rangoFromPreset(preset, new Date());
    setFechaDesde(r.desde);
    setFechaHasta(r.hasta);
  };

  const handleCargar = async (): Promise<void> => {
    if (!fechaDesde || !fechaHasta) {
      enqueueSnackbar('Indicá fecha desde y fecha hasta.', { variant: 'warning' });
      return;
    }
    if (comparar && (!compFechaDesde || !compFechaHasta)) {
      enqueueSnackbar('Completá las fechas comparativas.', { variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const data =
        reportType === 'resultados'
          ? await estadosContablesService.getEstadoResultados(params)
          : await estadosContablesService.getSituacionPatrimonial(params);
      setResult(data);
      enqueueSnackbar(`Informe cargado (${data.lineas.length} cuenta/s).`, { variant: 'success' });
    } catch (e) {
      setResult(null);
      enqueueSnackbar(estadosContablesService.parseError(e), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async (): Promise<void> => {
    try {
      const data =
        reportType === 'resultados'
          ? await estadosContablesService.getEstadoResultadosExportData(params)
          : await estadosContablesService.getSituacionPatrimonialExportData(params);
      if (data.lineas.length === 0) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'info' });
        return;
      }
      const rows =
        !detallado
          ? aggregateRubrosFromLineas(data.lineas).map((l) => ({
              Seccion: l.seccion,
              Rubro: l.nomRubro,
              Cuenta: '',
              ...(reportType === 'patrimonial'
                ? {
                    'Saldo inicial': l.saldoInicial,
                    'Saldo del período': l.saldoPeriodo,
                    Base: l.montoBase,
                    ...(comparar ? { 'Saldo ini. (comp.)': l.saldoInicialComparativo } : {}),
                    ...(comparar ? { 'Saldo período (comp.)': l.saldoPeriodoComparativo } : {})
                  }
                : {}),
              ...(reportType !== 'patrimonial' ? { Base: l.montoBase } : {}),
              Comparativo: l.montoComparativo,
              Variacion: l.variacionAbsoluta,
              VariacionPct: l.variacionPorcentual
            }))
          : data.lineas.map((l) => ({
              Seccion: l.seccion,
              Rubro: l.nomRubro,
              Cuenta: cuentaColumnLabel(l.nroCuenta, l.nomCuenta),
              ...(reportType === 'patrimonial'
                ? {
                    'Saldo inicial': l.saldoInicial ?? 0,
                    'Saldo del período': l.saldoPeriodo ?? 0,
                    Base: l.montoBase,
                    ...(comparar ? { 'Saldo ini. (comp.)': l.saldoInicialComparativo } : {}),
                    ...(comparar ? { 'Saldo período (comp.)': l.saldoPeriodoComparativo } : {})
                  }
                : {}),
              ...(reportType !== 'patrimonial' ? { Base: l.montoBase } : {}),
              Comparativo: l.montoComparativo,
              Variacion: l.variacionAbsoluta,
              VariacionPct: l.variacionPorcentual
            }));
      const ws = XLSX.utils.json_to_sheet(rows as unknown as Record<string, unknown>[]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Informe');
      XLSX.writeFile(wb, `${to.replace('/contabilidad/', '')}-${fechaDesde}_${fechaHasta}.xlsx`);
    } catch (e) {
      enqueueSnackbar(estadosContablesService.parseError(e), { variant: 'error' });
    }
  };

  const handlePdf = async (listado?: EstadoResultadosPdfListado): Promise<void> => {
    try {
      setPdfLoading(true);
      const blob =
        reportType === 'resultados'
          ? await estadosContablesService.getEstadoResultadosPdfBlob(paramsPdf, listado ?? 'periodo')
          : await estadosContablesService.getSituacionPatrimonialPdfBlob(paramsPdf);
      setPreviewPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } catch (e) {
      enqueueSnackbar(estadosContablesService.parseError(e), { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfButtonClick = (): void => {
    if (reportType === 'resultados') {
      setPdfDialogOpen(true);
      return;
    }
    void handlePdf();
  };

  const handleClosePdfPreview = (): void => {
    setPreviewPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const handleConfirmPdfListado = (listado: EstadoResultadosPdfListado): void => {
    setPdfDialogOpen(false);
    void handlePdf(listado);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/contabilidad/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {title}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="primary"
                startIcon={pdfLoading ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />}
                onClick={handlePdfButtonClick}
                disabled={pdfLoading}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                {pdfLoading
                  ? 'Generando PDF…'
                  : reportType === 'resultados'
                    ? 'Estado de Resultados'
                    : 'Situacion Patrimonial'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={() => void handleExportExcel()}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(245, 158, 11, 0.4)'
                  }
                }}
              >
                Exportar Excel
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                  {reportType === 'patrimonial' ? 'Fechas' : 'Período'}
                </Typography>
                {PRESET_CHIPS.map(({ id, label }) => (
                  <Chip
                    key={id}
                    label={label}
                    size="small"
                    onClick={() => aplicarPresetRango(id)}
                    color={rangoPreset === id ? 'primary' : 'default'}
                    variant={rangoPreset === id ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>
            </Grid>
            {reportType === 'patrimonial' && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Saldo inicial:</strong> acumulado hasta el día anterior a la fecha desde. <strong>Saldo del período:</strong> variación
                  entre inicial y cierre (base − saldo inicial). <strong>Base:</strong> posición al cierre (fecha hasta). La fecha desde delimita qué se
                  considera “inicial” frente al cierre.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="date"
                label="Fecha desde"
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                value={fechaDesde}
                helperText={reportType === 'patrimonial' ? 'Inicio del período para columna Saldo inicial' : undefined}
                onChange={(e) => {
                  setFechaDesde(e.target.value);
                  setRangoPreset('personalizado');
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                type="date"
                label={reportType === 'patrimonial' ? 'Posición al (fecha hasta)' : 'Fecha hasta'}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                value={fechaHasta}
                helperText={reportType === 'patrimonial' ? 'Saldos acumulados hasta este día inclusive' : undefined}
                onChange={(e) => {
                  setFechaHasta(e.target.value);
                  setRangoPreset('personalizado');
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel control={<Checkbox checked={comparar} onChange={(e) => setComparar(e.target.checked)} />} label="Comparar con otro período" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={<Checkbox checked={detallado} onChange={(e) => setDetallado(e.target.checked)} />}
                label="Detallado"
              />
            </Grid>
            {comparar && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField type="date" label="Comp. desde" InputLabelProps={{ shrink: true }} fullWidth size="small" value={compFechaDesde} onChange={(e) => setCompFechaDesde(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField type="date" label="Comp. hasta" InputLabelProps={{ shrink: true }} fullWidth size="small" value={compFechaHasta} onChange={(e) => setCompFechaHasta(e.target.value)} />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
                  onClick={() => void handleCargar()}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Cargar
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {!result && <Alert severity="info">Configurá filtros y pulsá Cargar.</Alert>}
          {result && (
            <>
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Sección</TableCell>
                      <TableCell>Rubro</TableCell>
                      <TableCell>Cuenta</TableCell>
                      {reportType === 'patrimonial' && <TableCell align="right">Saldo inicial</TableCell>}
                      {reportType === 'patrimonial' && <TableCell align="right">Saldo del período</TableCell>}
                      <TableCell align="right">Base</TableCell>
                      {reportType === 'patrimonial' && comparar && <TableCell align="right">Saldo ini. (comp.)</TableCell>}
                      {reportType === 'patrimonial' && comparar && <TableCell align="right">Saldo período (comp.)</TableCell>}
                      <TableCell align="right">Comparativo</TableCell>
                      <TableCell align="right">Variación</TableCell>
                      <TableCell align="right">Var.%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineasMostradas.flatMap((l) => {
                      if (l.tipo === 'cuenta') {
                        return (
                          <TableRow key={`${l.seccion}-${l.nroRubro}-${l.nroCuenta}`}>
                            <TableCell>{l.seccion}</TableCell>
                            <TableCell>{l.nomRubro}</TableCell>
                            <TableCell>{cuentaColumnLabel(l.nroCuenta, l.nomCuenta)}</TableCell>
                            {reportType === 'patrimonial' && (
                              <TableCell align="right">{fmt(l.saldoInicial ?? 0)}</TableCell>
                            )}
                            {reportType === 'patrimonial' && (
                              <TableCell align="right">{fmt(l.saldoPeriodo ?? 0)}</TableCell>
                            )}
                            <TableCell align="right">{fmt(l.montoBase)}</TableCell>
                            {reportType === 'patrimonial' && comparar && (
                              <TableCell align="right">{fmt(l.saldoInicialComparativo)}</TableCell>
                            )}
                            {reportType === 'patrimonial' && comparar && (
                              <TableCell align="right">{fmt(l.saldoPeriodoComparativo)}</TableCell>
                            )}
                            <TableCell align="right">{fmt(l.montoComparativo)}</TableCell>
                            <TableCell align="right">{fmt(l.variacionAbsoluta)}</TableCell>
                            <TableCell align="right">
                              {l.variacionPorcentual == null ? '—' : `${l.variacionPorcentual.toFixed(2)}%`}
                            </TableCell>
                          </TableRow>
                        );
                      }

                      const rk = rubroKey(l);
                      const expanded = expandedRubros.has(rk);
                      const cuentasDelRubro = lineasPorRubro.get(rk) ?? [];

                      const rubroRow = (
                        <TableRow
                          key={rk}
                          hover
                          aria-expanded={expanded}
                          onClick={() => toggleRubroExpand(rk)}
                          sx={{
                            bgcolor: 'rgba(25, 118, 210, 0.06)',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                              {expanded ? (
                                <KeyboardArrowDown sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
                              ) : (
                                <KeyboardArrowRight sx={{ fontSize: 15, color: 'text.secondary', flexShrink: 0 }} />
                              )}
                              {l.seccion}
                            </Box>
                          </TableCell>
                          <TableCell>{l.nomRubro}</TableCell>
                          <TableCell />
                          {reportType === 'patrimonial' && <TableCell align="right">{fmt(l.saldoInicial)}</TableCell>}
                          {reportType === 'patrimonial' && <TableCell align="right">{fmt(l.saldoPeriodo)}</TableCell>}
                          <TableCell align="right">{fmt(l.montoBase)}</TableCell>
                          {reportType === 'patrimonial' && comparar && (
                            <TableCell align="right">{fmt(l.saldoInicialComparativo)}</TableCell>
                          )}
                          {reportType === 'patrimonial' && comparar && (
                            <TableCell align="right">{fmt(l.saldoPeriodoComparativo)}</TableCell>
                          )}
                          <TableCell align="right">{fmt(l.montoComparativo)}</TableCell>
                          <TableCell align="right">{fmt(l.variacionAbsoluta)}</TableCell>
                          <TableCell align="right">
                            {l.variacionPorcentual == null ? '—' : `${l.variacionPorcentual.toFixed(2)}%`}
                          </TableCell>
                        </TableRow>
                      );

                      if (!expanded) {
                        return rubroRow;
                      }

                      const detailRows = cuentasDelRubro.map((c) => (
                        <TableRow key={`${rk}-${c.nroCuenta}`} sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ pl: 4 }}>{c.seccion}</TableCell>
                          <TableCell sx={{ pl: 1 }}>{c.nomRubro}</TableCell>
                          <TableCell>{cuentaColumnLabel(c.nroCuenta, c.nomCuenta)}</TableCell>
                          {reportType === 'patrimonial' && (
                            <TableCell align="right">{fmt(c.saldoInicial ?? 0)}</TableCell>
                          )}
                          {reportType === 'patrimonial' && (
                            <TableCell align="right">{fmt(c.saldoPeriodo ?? 0)}</TableCell>
                          )}
                          <TableCell align="right">{fmt(c.montoBase)}</TableCell>
                          {reportType === 'patrimonial' && comparar && (
                            <TableCell align="right">{fmt(c.saldoInicialComparativo)}</TableCell>
                          )}
                          {reportType === 'patrimonial' && comparar && (
                            <TableCell align="right">{fmt(c.saldoPeriodoComparativo)}</TableCell>
                          )}
                          <TableCell align="right">{fmt(c.montoComparativo)}</TableCell>
                          <TableCell align="right">{fmt(c.variacionAbsoluta)}</TableCell>
                          <TableCell align="right">
                            {c.variacionPorcentual == null ? '—' : `${c.variacionPorcentual.toFixed(2)}%`}
                          </TableCell>
                        </TableRow>
                      ));

                      return [rubroRow, ...detailRows];
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  mt: 2,
                  px: 2,
                  py: 1.25,
                  borderRadius: 1,
                  bgcolor: 'rgba(25, 118, 210, 0.09)',
                  borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {reportType === 'resultados' ? (
                    <>
                      Resultado del periodo: {fmt(result.totalGeneralBase)}
                    </>
                  ) : (
                    <>
                      Saldo inicial total: {fmt(result.totalGeneralSaldoInicial ?? 0)} · Saldo del período:{' '}
                      {fmt(result.totalGeneralSaldoPeriodo ?? 0)}
                      {comparar && (
                        <>
                          {' '}
                          · Saldo del período (comp.): {fmt(result.totalGeneralSaldoPeriodoComparativo)}
                        </>
                      )}{' '}
                      · Posición al cierre (base): {fmt(result.totalGeneralBase)}
                    </>
                  )}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={pdfDialogOpen} onClose={() => setPdfDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Elegí el listado PDF</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleccioná qué listado querés emitir en PDF.
          </Typography>
          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleConfirmPdfListado('periodo')}
              startIcon={<DateRangeIcon sx={{ fontSize: 18 }} />}
              sx={{
                py: 1.5,
                textTransform: 'none',
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: 2,
                borderColor: '#e0e0e0',
                '& .MuiButton-startIcon': { mr: 1 },
                '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(25, 118, 210, 0.04)' }
              }}
            >
              <Box sx={{ display: 'block', textAlign: 'left' }}>
                <Typography component="span" display="block" fontWeight={600} color="text.primary">
                  Estado de resultados del período
                </Typography>
                <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Formato clásico por cuentas con columnas Debe, Haber y Saldo.
                </Typography>
              </Box>
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleConfirmPdfListado('mensual')}
              startIcon={<DateRangeIcon sx={{ fontSize: 18 }} />}
              sx={{
                py: 1.5,
                textTransform: 'none',
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: 2,
                borderColor: '#e0e0e0',
                '& .MuiButton-startIcon': { mr: 1 },
                '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(25, 118, 210, 0.04)' }
              }}
            >
              <Box sx={{ display: 'block', textAlign: 'left' }}>
                <Typography component="span" display="block" fontWeight={600} color="text.primary">
                  Estado de resultados del período por mes
                </Typography>
                <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Matriz por mes: rubros en filas y cada mes del período en columnas (saldo mensual); opción Detallado agrega cuentas por rubro.
                </Typography>
              </Box>
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleConfirmPdfListado('comparativo')}
              startIcon={<DateRangeIcon sx={{ fontSize: 18 }} />}
              sx={{
                py: 1.5,
                textTransform: 'none',
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: 2,
                borderColor: '#e0e0e0',
                '& .MuiButton-startIcon': { mr: 1 },
                '&:hover': { borderColor: '#1976d2', bgcolor: 'rgba(25, 118, 210, 0.04)' }
              }}
            >
              <Box sx={{ display: 'block', textAlign: 'left' }}>
                <Typography component="span" display="block" fontWeight={600} color="text.primary">
                  Estado de resultados comparativo
                </Typography>
                <Typography component="span" variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Compara período base vs período comparativo con variaciones.
                </Typography>
              </Box>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPdfDialogOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {previewPdfUrl ? (
        <PDFViewer
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          title={title}
          documentId="informe-estado-contable"
          documentType="informeCliente"
          downloadFileName={`${to.replace('/contabilidad/', '')}-${fechaDesde}_${fechaHasta}.pdf`}
        />
      ) : null}
    </Box>
  );
};

export default EstadoContableReport;
